import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Store } from "lucide-react";
import { http, fmtErr } from "../lib/api";

export default function Contact() {
  const location = useLocation();
  const initialType = new URLSearchParams(location.search).get("type") === "wholesaler" ? "wholesaler" : "general";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", contact_type: initialType, business_name: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/contact", form);
      setDone(true);
      toast.success("Message sent!");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F] transition-colors";
  const isWholesaler = form.contact_type === "wholesaler";

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="contact-root">
      <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Contact</div>
      <h1 className="font-heading text-6xl uppercase tracking-wide">Get in touch</h1>
      <p className="mt-4 text-lg text-[#5C5A56]">Question about a product? Custom order? Bulk pricing? Drop us a line.</p>

      <div className="mt-8 grid grid-cols-2 gap-3" data-testid="contact-type-toggle">
        <button
          onClick={() => setForm({...form, contact_type: "general"})}
          className={`p-4 border-2 text-left transition-colors ${!isWholesaler ? "border-[#D32F2F] bg-[#D32F2F]/5" : "border-[#E5D8C1] hover:border-[#1A1A1A]"}`}
          data-testid="contact-type-general"
        >
          <ShoppingBag size={20} className={!isWholesaler ? "text-[#D32F2F]" : "text-[#5C5A56]"} />
          <div className="font-heading text-lg uppercase tracking-wide mt-2">General inquiry</div>
          <div className="text-xs text-[#5C5A56] mt-1">Product questions, order help, custom requests</div>
        </button>
        <button
          onClick={() => setForm({...form, contact_type: "wholesaler"})}
          className={`p-4 border-2 text-left transition-colors ${isWholesaler ? "border-[#D32F2F] bg-[#D32F2F]/5" : "border-[#E5D8C1] hover:border-[#1A1A1A]"}`}
          data-testid="contact-type-wholesaler"
        >
          <Store size={20} className={isWholesaler ? "text-[#D32F2F]" : "text-[#5C5A56]"} />
          <div className="font-heading text-lg uppercase tracking-wide mt-2">Wholesaler support</div>
          <div className="text-xs text-[#5C5A56] mt-1">Existing wholesaler? Reorders, invoicing, escalations</div>
        </button>
      </div>

      {done ? (
        <div className="mt-10 bg-[#7CB342]/15 border border-[#7CB342] p-8" data-testid="contact-success">
          <div className="font-heading text-2xl uppercase tracking-wide">Message received</div>
          <p className="text-[#5C5A56] mt-2">We&apos;ll get back to you within 24 hours.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 bg-[#FDF4E3] border border-[#E5D8C1] p-8 md:p-10 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block"><span className="text-xs uppercase tracking-widest">Name *</span>
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={input + " mt-2"} data-testid="contact-name" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Email *</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={input + " mt-2"} data-testid="contact-email" />
            </label>
          </div>
          {isWholesaler && (
            <label className="block"><span className="text-xs uppercase tracking-widest">Business name *</span>
              <input required value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className={input + " mt-2"} placeholder="e.g. Main St. C-Store" data-testid="contact-business" />
            </label>
          )}
          <label className="block"><span className="text-xs uppercase tracking-widest">Phone</span>
            <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={input + " mt-2"} data-testid="contact-phone" />
          </label>
          <label className="block"><span className="text-xs uppercase tracking-widest">Message *</span>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className={input + " mt-2 resize-none"} placeholder={isWholesaler ? "Tell us about your reorder, invoice question, or account issue…" : "How can we help?"} data-testid="contact-message" />
          </label>
          <button type="submit" disabled={loading} className="w-full bg-[#D32F2F] text-white hover:bg-[#B71C1C] py-4 uppercase tracking-widest font-heading text-lg disabled:opacity-60 flex items-center justify-center gap-2" data-testid="contact-submit">
            {loading && <Loader2 className="animate-spin" size={18} />} Send message
          </button>
        </form>
      )}
    </div>
  );
}
